/**
 * testing-grid — the one column template every row of the Testing view shares
 * (header, path band, child rows), so names, ages, money and verdicts line up
 * down the whole page.
 *
 * The ELEMENT column is bounded, not elastic: giving it the slack left a dead
 * gap across the middle of a wide screen. The slack goes to NEXT RUN instead —
 * the only column whose content actually varies in length.
 *
 * Below `md` the columns stack and each value states its own label, so a narrow
 * window degrades into a readable list rather than a horizontal scrollbar.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, ReactNode } from 'react';

import { cn } from '@/lib/utils';

/** Element · Under test · Next run · Actions · timeline button.
 *  Profit/ROI were dropped: the live rows are judged by the rotation's own
 *  verdict (Next run), not by a two-metric snapshot. */
const COLUMNS =
  'md:grid-cols-[minmax(10rem,20rem)_4.5rem_minmax(9rem,1fr)_13rem_1.5rem]';

export type RowTone = 'requeue' | 'eliminate' | 'held' | 'none';

/**
 * A row the next run touches is tinted AND railed in the verdict's own colour —
 * a hairline alone was too easy to miss, and painting a requeue red said
 * "destroyed" about an element that is only going back in the queue.
 */
const TONE: Record<RowTone, string> = {
  requeue: 'border-l-primary bg-primary/[0.07] hover:bg-primary/[0.12]',
  eliminate: 'border-l-destructive bg-destructive/[0.07] hover:bg-destructive/[0.12]',
  held: 'border-l-warning bg-warning/[0.07] hover:bg-warning/[0.12]',
  none: 'border-l-transparent',
};

/** A row on the shared template; `tone` colours the 2px status rail. */
export const GridRow: FC<{
  children: ReactNode;
  tone?: RowTone;
  className?: string;
  'data-testid'?: string;
}> = ({ children, tone = 'none', className, ...rest }) => (
  <div
    {...rest}
    className={cn(
      'grid grid-cols-1 items-center gap-x-4 gap-y-0.5 border-l-[3px] px-2 py-1 transition-colors md:gap-y-0',
      COLUMNS,
      TONE[tone],
      className,
    )}
  >
    {children}
  </div>
);

/**
 * One cell. `label` is what the column header says; it is repeated inline only
 * on small screens, where there is no header to read the value against.
 */
export const Cell: FC<{
  children: ReactNode;
  label?: string;
  align?: 'right';
  className?: string;
}> = ({ children, label, align, className }) => (
  <div
    className={cn(
      'flex min-w-0 items-center gap-2 text-sm',
      align === 'right' && 'md:justify-end md:text-right',
      className,
    )}
  >
    {label ? (
      <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground md:hidden">
        {label}
      </span>
    ) : null}
    {children}
  </div>
);

export const COLUMN_LABELS = {
  element: 'Element',
  underTest: 'Under test',
  nextRun: 'Next run',
  actions: 'Actions',
} as const;

/** The header strip — rendered once above all groups, never per section. */
export const TestingGridHeader: FC = () => (
  <GridRow
    data-testid="testing-grid-header"
    className="hidden border-b border-border/60 py-1 md:grid"
  >
    {(['element', 'underTest', 'nextRun', 'actions'] as const).map((key) => (
      <span
        key={key}
        className={cn(
          'text-[10px] font-semibold uppercase tracking-wide text-muted-foreground',
          key === 'actions' && 'text-right',
        )}
      >
        {COLUMN_LABELS[key]}
      </span>
    ))}
    <span className="sr-only">Timeline</span>
  </GridRow>
);
